import { Component } from '@theme/component';

/**
 * Drives the market switcher's Insta360-style modal.
 *
 * The header/footer variants render a native <dialog> (data-ref="dialog")
 * containing the "Choose Your Country or Region" panel. Opening is a button
 * click (showModal), closing is the close button, backdrop click or Escape.
 * Live search filters the continent-grouped market cards by country name and
 * shows a "Not found" empty state.
 *
 * Only the modal variants (header / footer) use this component; the
 * mobile-drawer variant renders a plain <details> and needs no JS.
 */
export class LocalizationSwitcherComponent extends Component {
  requiredRefs = ['dialog', 'trigger', 'close', 'search', 'list', 'empty'];

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.dialog || !this.refs.trigger) return;

    this.refs.trigger.addEventListener('click', this.#onOpen);
    this.refs.dialog.addEventListener('click', this.#onDialogClick);
    if (this.refs.close) {
      this.refs.close.addEventListener('click', this.#onClose);
    }
    if (this.refs.search) {
      this.refs.search.addEventListener('input', this.#onSearch);
      this.refs.search.addEventListener('keydown', this.#onSearchKey);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.refs.dialog || !this.refs.trigger) return;

    this.refs.trigger.removeEventListener('click', this.#onOpen);
    this.refs.dialog.removeEventListener('click', this.#onDialogClick);
    this.refs.close?.removeEventListener('click', this.#onClose);
    this.refs.search?.removeEventListener('input', this.#onSearch);
    this.refs.search?.removeEventListener('keydown', this.#onSearchKey);
  }

  #onOpen = () => {
    this.#resetSearch();
    if (typeof this.refs.dialog.showModal === 'function') {
      this.refs.dialog.showModal();
    } else {
      this.refs.dialog.setAttribute('open', '');
    }
    this.refs.trigger?.setAttribute('aria-expanded', 'true');
  };

  #onClose = () => {
    if (typeof this.refs.dialog.close === 'function') {
      this.refs.dialog.close();
    } else {
      this.refs.dialog.removeAttribute('open');
    }
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
  };

  #onDialogClick = (event) => {
    // Light-dismiss when clicking the dimmed backdrop area.
    if (event.target === this.refs.dialog) {
      this.#onClose();
    }
  };

  #onSearchKey = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #onSearch = () => {
    const value = (this.refs.search?.value || '').trim().toLowerCase();
    let anyVisible = false;

    for (const group of this.querySelectorAll('.localization-switcher__group')) {
      let groupVisible = false;
      for (const option of group.querySelectorAll('.localization-switcher__option')) {
        const hit = value === '' || (option.dataset.name || '').includes(value);
        option.hidden = !hit;
        if (hit) groupVisible = true;
      }
      group.hidden = !groupVisible;
      if (groupVisible) anyVisible = true;
    }

    if (this.refs.empty) {
      this.refs.empty.hidden = anyVisible;
    }
  };

  #resetSearch() {
    if (!this.refs.search) return;
    this.refs.search.value = '';
    this.#onSearch();
  }
}

if (!customElements.get('localization-switcher-component')) {
  customElements.define('localization-switcher-component', LocalizationSwitcherComponent);
}